import { create } from 'zustand';
import { meetingsApi } from '../services/api/meetingsApi';

// Central initial state for easy reset
const initialState = {
  meetings: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  searchTerm: '',
  orderBy: 'scheduledAt',
  orderDirection: 'desc',
  limit: 30,
  // Filters
  statusFilter: null,
  participantIds: null,
  datePreset: null,
  dateFrom: null,
  dateTo: null,
  typeIds: null,
  actionItemsFilter: null,
  talkingPointsFilter: null,
  // UI State
  scrollPosition: 0,
  lastCardId: null,
  isReturningFromDetails: false,
  uiSearchInput: '',
  selectedStatuses: [],
  selectedParticipants: [],
  selectedTypes: [],
};

// Factory function to create meeting stores with different API endpoints
const createMeetingsStore = (apiFn) => create((set, get) => {
  // Helper: param construction
  const buildParams = (overrides = {}) => {
    const state = get();
    return {
      limit: state.limit,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.pageInfo.endCursor,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      search: overrides.search !== undefined ? overrides.search : state.searchTerm,
      statusFilter: overrides.statusFilter !== undefined ? overrides.statusFilter : state.statusFilter,
      participantIds: overrides.participantIds !== undefined ? overrides.participantIds : state.participantIds,
      datePreset: overrides.datePreset !== undefined ? overrides.datePreset : state.datePreset,
      dateFrom: overrides.dateFrom !== undefined ? overrides.dateFrom : state.dateFrom,
      dateTo: overrides.dateTo !== undefined ? overrides.dateTo : state.dateTo,
      typeIds: overrides.typeIds !== undefined ? overrides.typeIds : state.typeIds,
      actionItemsFilter: overrides.actionItemsFilter !== undefined ? overrides.actionItemsFilter : state.actionItemsFilter,
      talkingPointsFilter: overrides.talkingPointsFilter !== undefined ? overrides.talkingPointsFilter : state.talkingPointsFilter,
      ...overrides,
    };
  };

  // Generic fetcher (reset/append)
  const fetchMeetings = async ({ reset = false, overrides = {} } = {}) => {
    if (get().isLoading || get().isLoadingMore) return null;

    if (reset) set({ isLoading: true, error: null });
    else set({ isLoadingMore: true, error: null });

    const params = buildParams(reset ? { ...overrides, cursor: null } : overrides);

    try {
      const response = await apiFn(params);

      if (response) {
        const newMeetings = reset ? response.items : [...get().meetings, ...response.items];
        const pageInfo = {
          ...response.pageInfo,
          hasNextPage: newMeetings.length < response.pageInfo.totalCount,
        };
        set({
          meetings: newMeetings,
          pageInfo,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false, isLoadingMore: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load meetings',
        isLoading: false,
        isLoadingMore: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setScrollPosition: (position) => set({ scrollPosition: position }),
    setLastCardId: (id) => set({ lastCardId: id }),
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setUISearchInput: (input) => set({ uiSearchInput: input }),

    // Combined filter setters (like IDP pattern - atomic UI + filter update)
    setSelectedParticipants: (participants) => {
      set({ selectedParticipants: participants });
      const ids = participants.length > 0 ? participants.map(p => p.id).join(',') : null;
      get().setParticipantIds(ids);
    },
    setSelectedTypes: (types) => {
      set({ selectedTypes: types });
      const ids = types.length > 0 ? types.map(t => t.id).join(',') : null;
      get().setTypeIds(ids);
    },
    setSelectedStatuses: (statuses) => {
      set({ selectedStatuses: statuses });
      const ids = statuses.length > 0 ? statuses.map(s => s.id).join(',') : null;
      get().setStatusFilter(ids);
    },

    // Filters
    setSearchTerm: (searchTerm, skipRefetch = false) => {
      set({ searchTerm });
      if (!skipRefetch) get().fetchMeetingsWithReset({ search: searchTerm });
    },
    setOrderBy: (orderBy, skipRefetch = false) => {
      set({ orderBy });
      if (!skipRefetch) get().fetchMeetingsWithReset({ orderBy });
    },
    setOrderDirection: (orderDirection, skipRefetch = false) => {
      set({ orderDirection });
      if (!skipRefetch) get().fetchMeetingsWithReset({ orderDirection });
    },
    setLimit: (limit, skipRefetch = false) => {
      set({ limit });
      if (!skipRefetch) get().fetchMeetingsWithReset({ limit });
    },
    setStatusFilter: (statusFilter, skipRefetch = false) => {
      set({ statusFilter });
      if (!skipRefetch) get().fetchMeetingsWithReset({ statusFilter });
    },
    setParticipantIds: (participantIds, skipRefetch = false) => {
      set({ participantIds });
      if (!skipRefetch) get().fetchMeetingsWithReset({ participantIds });
    },
    setDatePreset: (datePreset, skipRefetch = false) => {
      // Clear custom dates when using preset
      set({ datePreset, dateFrom: null, dateTo: null });
      if (!skipRefetch) get().fetchMeetingsWithReset({ datePreset, dateFrom: null, dateTo: null });
    },
    setDateRange: (dateFrom, dateTo, skipRefetch = false) => {
      // Clear preset when using custom dates
      set({ dateFrom, dateTo, datePreset: null });
      if (!skipRefetch) get().fetchMeetingsWithReset({ dateFrom, dateTo, datePreset: null });
    },
    setTypeIds: (typeIds, skipRefetch = false) => {
      set({ typeIds });
      if (!skipRefetch) get().fetchMeetingsWithReset({ typeIds });
    },
    setActionItemsFilter: (actionItemsFilter, skipRefetch = false) => {
      set({ actionItemsFilter });
      if (!skipRefetch) get().fetchMeetingsWithReset({ actionItemsFilter });
    },
    setTalkingPointsFilter: (talkingPointsFilter, skipRefetch = false) => {
      set({ talkingPointsFilter });
      if (!skipRefetch) get().fetchMeetingsWithReset({ talkingPointsFilter });
    },

    // Data actions
    fetchMeetingsWithReset: (overrides = {}) => fetchMeetings({ reset: true, overrides }),
    fetchMeetingsLoadMore: () => fetchMeetings({ reset: false }),

    updateMeeting: (updatedMeeting) => {
      if (!updatedMeeting || !updatedMeeting.id) {
        return false;
      }
      const { meetings, pageInfo } = get();
      const index = meetings.findIndex(meeting => meeting.id === updatedMeeting.id);

      if (index !== -1) {
        // Update existing meeting
        const updatedMeetings = [...meetings];
        updatedMeetings[index] = { ...updatedMeetings[index], ...updatedMeeting };
        set({ meetings: updatedMeetings });
        return true;
      } else {
        // Add new meeting and increment total count
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1
        };
        set({
          meetings: [updatedMeeting, ...meetings],
          pageInfo: updatedPageInfo
        });
        return true;
      }
    },

    removeMeeting: (meetingId) => {
      if (!meetingId) return false;

      const { meetings, pageInfo } = get();
      const index = meetings.findIndex(meeting => meeting.id === meetingId);

      if (index !== -1) {
        const updatedMeetings = meetings.filter(meeting => meeting.id !== meetingId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1)
        };
        set({
          meetings: updatedMeetings,
          pageInfo: updatedPageInfo
        });
        return true;
      }
      return false;
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchTerm: '',
        uiSearchInput: '',
        selectedStatuses: [],
        statusFilter: null,
        participantIds: null,
        selectedParticipants: [],
        datePreset: null,
        dateFrom: null,
        dateTo: null,
        typeIds: null,
        selectedTypes: [],
        actionItemsFilter: null,
        talkingPointsFilter: null,
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Infinite loader helper
    shouldLoadMore: (lastVisibleIndex, threshold = 10) => {
      const { meetings, pageInfo, isLoadingMore } = get();
      return !isLoadingMore &&
             pageInfo.hasNextPage &&
             lastVisibleIndex >= meetings.length - threshold;
    },

    getMeetingByIndex: (index) => {
      const { meetings } = get();
      return meetings[index] || null;
    },
  };
});

// Default export for repository view (all meetings)
const useMeetingsStoreRepository = createMeetingsStore(meetingsApi.getAllMeetings);
export default useMeetingsStoreRepository;

// Export for MyMeetings view (user's meetings)
export const useMeetingsStoreMyMeetings = createMeetingsStore(meetingsApi.getMyMeetings);
